import { BatteryMapper, ClientMapper } from '.';
import { BATTERY_SIZING_STATUS } from '../constants/battery-sizing.constants';
import {
  BatterySizingRangeResult,
  MaterialBom,
  Project,
  ProjectCreate,
  ProjectForList,
  ProjectSizingToolResult,
  ProjectSizingToolResultCreate,
  StrapiImage,
} from '../models';
import { ProjectCreateCleaner } from '../utils/project-create-cleaner.util';

export class ProjectMapper {
  static projectDtoToProjectForList(projectDto: Project): ProjectForList {
    const { sizingTool, manualSizing, ...projectRest } = projectDto;

    return {
      ...projectRest,
      sizingTool: sizingTool ?? undefined,
      manualSizing: manualSizing ?? undefined,
      battery: projectRest.battery
        ? BatteryMapper.batteryDtoToBatteryMinimal(projectRest.battery)
        : undefined,
      client: projectRest.client
        ? ClientMapper.clientDtoToClientMinimal(projectRest.client)
        : undefined,
    };
  }

  static prepareProjectCreate(
    projectCreate: Partial<ProjectCreate>,
    shouldClean = true,
  ): Partial<ProjectCreate> {
    const project = { ...projectCreate };

    if (project.documentId) {
      delete project.documentId;
    }

    const cleanedProject = shouldClean
      ? ProjectCreateCleaner.clean(project)
      : project;

    // Map MaterialBom
    if (cleanedProject.bom) {
      cleanedProject.bom = this.prepareBomsCreate(cleanedProject.bom);
    }

    // Normalize sizing tool results
    if (cleanedProject.sizingToolResults) {
      cleanedProject.sizingToolResults = this.prepareSizingResultsCreate(
        cleanedProject.sizingToolResults,
      );
    }

    return cleanedProject;
  }

  static mapProjectResultsToRanges(
    results: ProjectSizingToolResult[],
  ): BatterySizingRangeResult[] {
    return results.map((result) => ({
      rangeCode: result.battery_range?.code ?? '',
      rangeName: result.battery_range?.name ?? null,
      range: result.battery_range ?? undefined,
      status: result.resultStatus ?? BATTERY_SIZING_STATUS.Feasible,
      batteryUnitPrice: result.batteryUnitPrice ?? null,
      batteryTotalPrice: result.batteryTotalPrice ?? null,
      configuration: result.configuration
        ? {
            batteryDocumentId:
              result.configuration.battery?.documentId ??
              result.configuration.batteryDocumentId ??
              '',
            batteryPartNumber: result.configuration.battery?.partNumber ?? '',
            parallels: result.configuration.parallels ?? 0,
            seriesBlocks: result.configuration.seriesBlocks ?? 0,
            c10RequiredAh: result.configuration.c10RequiredAh ?? 0,
            c10NominalAh: result.configuration.c10NominalAh ?? 0,
            c10OutputLabelAh: result.configuration.c10OutputLabelAh ?? null,
            ventilation: result.configuration.ventilation,
            cableRecommendation: result.configuration.cableRecommendation,
            designFactor: result.configuration.designFactor,
            ageingFactor: result.configuration.ageingFactor,
            temperatureFactor: result.configuration.temperatureFactor,
            totalCorrectionFactor: result.configuration.totalCorrectionFactor,
            maxSectionSize: result.configuration.maxSectionSize ?? 0,
            requiredKt: result.configuration.requiredKt ?? 0,
            offeredKt: result.configuration.offeredKt ?? 0,
            realBackupMinutes: result.configuration.realBackupMinutes ?? 0,
            diagnostics: {
              interpolation: {
                timeLower: 0,
                timeUpper: 0,
                voltageLower: 0,
                voltageUpper: 0,
                nodes: [],
                method: 'linear',
              },
              temperature: {
                minutesLower: 0,
                minutesUpper: 0,
                temperatureLower: 0,
                temperatureUpper: 0,
                factors: [],
                method: 'linear',
              },
              perStringLoad: 0,
              perStringLoadUnit: 'watts',
              searchIterations: 0,
            },
          }
        : undefined,
    }));
  }

  static prepareBomsCreate(bom: MaterialBom[]) {
    return bom.map((bom) => {
      return this.prepareBomCreate(bom);
    });
  }

  static prepareBomCreate(materialBom: MaterialBom) {
    const bom = { ...materialBom };
    delete bom.id;
    delete bom.clientRowId;

    if (bom.schemaFile) {
      bom.schemaFile = this.prepareSchemaFileCreate(bom.schemaFile);
    }

    return bom;
  }

  static prepareSchemaFileCreate(schemaFile: StrapiImage) {
    schemaFile = { ...schemaFile };
    if (schemaFile.documentId) {
      delete schemaFile.documentId;
    }
    return schemaFile;
  }

  private static prepareSizingResultsCreate(
    results: ProjectSizingToolResultCreate[],
  ) {
    return results.map((result) => {
      const normalized: ProjectSizingToolResultCreate = { ...result };
      delete normalized.id;

      if (normalized.configuration) {
        const parsedConfig = { ...normalized.configuration };
        delete parsedConfig.id;
        normalized.configuration = parsedConfig;
      }
      return normalized;
    });
  }
}
